import React from 'react';
import { ConfigProvider } from 'antd';
import { useTheme } from '../../../theme/ThemeContext';
import { AgenticHITLCard } from '../AskAsperAgentic.types';

export interface HITLCardProps {
    card: AgenticHITLCard;
    'data-test'?: string;
    'data-test-id'?: string;
}

/**
 * HITLCard - Human in the Loop card component for interrupts
 *
 * This component provides a flexible, extendible structure for HITL cards
 * that can appear as interrupts in the conversation flow. The card accepts
 * custom content and action handlers.
 *
 * HITL cards are used by the chat agent to request user input, confirmations,
 * or other interactions that require human intervention.
 */
const HITLCard: React.FC<HITLCardProps> = ({ card, 'data-test': dataTest, 'data-test-id': dataTestId }) => {
    const theme = useTheme();

    return (
        <ConfigProvider theme={theme}>
            <div
                className="bg-[#FFF7ED] rounded-lg border border-[#FED7AA] p-4"
                data-test={dataTest}
                data-test-id={dataTestId}
                data-hitl-card
                data-hitl-type={card.type}
            >
                {/* Custom Content */}
                {card.content ? (
                    typeof card.content === 'string' ? (
                        <div className="font-['Open_Sans'] text-[16px] leading-[20px] text-[rgba(0,0,0,0.88)]">
                            {card.content}
                        </div>
                    ) : (
                        card.content
                    )
                ) : (
                    <div className="font-['Open_Sans'] text-[16px] leading-[20px] text-[rgba(0,0,0,0.88)]">
                        {card.title && <h4 className="font-semibold mb-2">{card.title}</h4>}
                        {card.message && <p>{card.message}</p>}
                    </div>
                )}

                {/* Actions */}
                {card.actions && card.actions.length > 0 && (
                    <div className="flex gap-2 mt-4">
                        {card.actions.map((action, index) => (
                            <button
                                key={index}
                                onClick={action.onClick}
                                className={`px-4 py-2 rounded-lg font-['Open_Sans'] text-[16px] leading-[20px] transition-colors ${
                                    action.primary
                                        ? 'bg-[#7C3AED] text-white hover:bg-[#6D28D9]'
                                        : 'bg-white text-[#7C3AED] border border-[#7C3AED] hover:bg-[#F3E8FF]'
                                }`}
                                data-test-id={`${dataTestId}-action-${index}`}
                            >
                                {action.label}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </ConfigProvider>
    );
};

export default HITLCard;
